import React, { useState, useEffect } from 'react';
import { ExternalLink, AlertCircle, CheckCircle, Info, AlertTriangle } from 'lucide-react';
import api from '../utils/api';

const HotNewsMarquee = ({ className = '' }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchNews();
    // Refresh every 5 minutes
    const interval = setInterval(fetchNews, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      const res = await api.get('/hot-news/active');
      setNews(res.data || []);
    } catch (error) {
      console.error('Failed to fetch hot news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'warning': return <AlertTriangle className="w-4 h-4 text-yellow-400" />;
      case 'urgent': return <AlertCircle className="w-4 h-4 text-red-400" />;
      default: return <Info className="w-4 h-4 text-blue-400" />;
    }
  };

  const getTypeStyles = (type) => {
    switch (type) {
      case 'success': return 'bg-green-500/10 border-green-500/30';
      case 'warning': return 'bg-yellow-500/10 border-yellow-500/30';
      case 'urgent': return 'bg-red-500/10 border-red-500/30 animate-pulse';
      default: return 'bg-blue-500/10 border-blue-500/30';
    }
  };

  if (loading || news.length === 0) return null;

  // Duplicate news for seamless scrolling
  const duplicatedNews = [...news, ...news];

  return (
    <div 
      className={`relative overflow-hidden ${className}`}
      data-testid="hot-news-marquee"
    >
      <div className="absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-background to-transparent z-10" />
      <div className="absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-background to-transparent z-10" />
      
      <div className="marquee-container py-2">
        <div className="marquee-content flex gap-8 animate-marquee">
          {duplicatedNews.map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              className={`flex items-center gap-2 px-4 py-1.5 rounded-full border whitespace-nowrap ${getTypeStyles(item.type)}`}
            >
              {getIcon(item.type)}
              <span className="text-sm font-medium">{item.title}</span>
              {item.content && (
                <span className="text-sm text-muted-foreground">- {item.content}</span>
              )}
              {item.link && (
                <a
                  href={item.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-yellow-400 hover:text-yellow-300 flex items-center gap-1 ml-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  {item.link_text || 'Learn More'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>

      <style jsx global>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
          will-change: transform;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        @media (max-width: 768px) {
          .animate-marquee {
            animation-duration: 12s;
          }
        }
      `}</style>
    </div>
  );
};

export default HotNewsMarquee;
